import React, { useEffect, useReducer } from 'react';

import _ from 'lodash';
import PropTypes from 'prop-types';
import { initialState, reducer } from 'reducers/collectionReducer';

import CircleQuestion from 'components/application/CircleQuestion';
import Tooltip from 'components/application/Tooltip';

import { useBreadBoard } from 'components/contexts/Toaster';
import Attachments from 'components/training/attachments/components/Attachments';

import {
  getCollection,
  getMember,
  initialMember,
} from 'components/helpers/resources/attachments';

export default function AttachmentsField({
  isVisible = true,
  label,
  maxUploads,
  name,
  onChange,
  onUploadSuccess,
  seedAttachments = { data: [], included: [] },
  tooltip,
  value,
  valueFormat = 'id',
}) {
  const { addInedibleToast } = useBreadBoard();

  const [attachments, dispatch] = useReducer(reducer, initialState);

  const handleAttachmentChange = ({ attachment }) => {
    dispatch({
      event: { target: { name: 'attachedProcessed', value: false } },
      key: attachment.key,
      type: 'CHANGE',
    });
  };

  const handleAttachmentCreate = ({ attachment, fileType, uploadKey }) => {
    axios
      .post('/attachments', {
        additional_processing: fileType === 'pdf',
        attachment: { attached_key: uploadKey },
        type: fileType,
      })
      .then((response) => {
        const member = getMember({
          attachment: response.data.data,
          coverImages: response.data.included.filter(
            (obj) => obj.type === 'coverImage',
          ),
        });

        dispatch({ type: 'REPLACE', payload: member, key: attachment.key });
      })
      .catch(addInedibleToast);
  };

  const handleAttachmentRotateClick = ({ attachment }) => {
    handleAttachmentChange({ attachment });
  };

  const handleAttachmentRotateSuccess = ({ attachment, payload }) => {
    dispatch({ type: 'REPLACE', payload, key: attachment.key });
  };

  const handleAttachmentDelete = ({ attachment }) => {
    handleAttachmentChange({ attachment });

    dispatch({ type: 'REMOVE', key: attachment.key });
  };

  const handleAttachmentUpload = (file) => {
    dispatch({ type: 'ADD', payload: { ...initialMember, file } });
  };

  const handlePollingSuccess = ({ attachment, response }) => {
    const member = getMember({
      attachment: response.data,
      coverImages: response.included.filter((obj) => obj.type === 'coverImage'),
    });

    if (onUploadSuccess) onUploadSuccess();
    dispatch({ type: 'REPLACE', payload: member, key: attachment.key });
  };

  useEffect(() => {
    if (isVisible) {
      const payload =
        seedAttachments.data.length === 0 ? [] : getCollection(seedAttachments);

      dispatch({ type: 'LOAD', payload });
    }
  }, [isVisible]);

  useEffect(() => {
    const areAllAttachmentsPersisted = attachments.collection.every(
      (attachment) => attachment.id,
    );

    if (attachments.collection.length === 0 || areAllAttachmentsPersisted) {
      const newValue = attachments.collection.map((attachment) =>
        valueFormat === 'hashid' ? attachment.hashid : Number(attachment.id),
      );

      if (JSON.stringify(value) !== JSON.stringify(newValue)) {
        onChange({ target: { name, value: newValue } });
      }
    }
  }, [JSON.stringify(attachments.collection)]);

  return (
    <div className='attachments-field'>
      <div className='form-group'>
        <label className='field__label tw-font-medium' htmlFor={name}>
          {label}
          {tooltip && (
            <Tooltip placement='top' tooltip={tooltip} trigger='hover'>
              <CircleQuestion />
            </Tooltip>
          )}
        </label>
        {attachments.loaded && (
          <div className='attachments__wrapper tw-border-grey-300'>
            <Attachments
              attachments={attachments.collection}
              maxUploads={maxUploads}
              onCreate={handleAttachmentCreate}
              onDelete={handleAttachmentDelete}
              onPollingSuccess={handlePollingSuccess}
              onRotateClick={handleAttachmentRotateClick}
              onRotateSuccess={handleAttachmentRotateSuccess}
              onUpload={handleAttachmentUpload}
            />
          </div>
        )}
      </div>
    </div>
  );
}

AttachmentsField.propTypes = {
  isVisible: PropTypes.bool,
  label: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  seedAttachments: PropTypes.object,
  tooltip: PropTypes.string,
  value: PropTypes.array.isRequired,
  valueFormat: PropTypes.oneOf(['id', 'hashid']),
};
